import React, { useEffect } from 'react';
import { createContext, useState } from 'react';
import { TokenResponse } from '@react-oauth/google';
import { AuthContextType } from './index.d';
import { isEmpty } from 'lodash';
import { CredsAuthResponse, GoogleTokenInfo } from './auth.type';
import { toast } from 'react-toastify';

const AuthContext = createContext<AuthContextType | {}>({});

const GoogleCredentialEndpoint =
  process.env.REACT_APP_GOOGLE_CREDENTIALS_ENDPOINT ||
  'https://www.googleapis.com/oauth2/v1/tokeninfo';

const WHITELIST_DOMAIN_EMAILS = [
  'miva.edu.ng',
  'ulesson.com',
  'miva.university',
];

const PassAccessTokenProvider: any = {};
export const AuthProvider = ({ children, isWeb }: any) => {
  const [auth, setAuth] = useState({}) || {};
  PassAccessTokenProvider.auth = auth;
  PassAccessTokenProvider.setAuth = setAuth;

  useEffect(() => {
    if (isWeb) {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      setAuth(auth);
    } else {
      chrome.storage.local.get().then((r) => {
        console.log('AuthDataExt ', r);
        const authDataExt = r['auth'] ? JSON.parse(r['auth']) : {};
        if (!isEmpty(authDataExt)) {
          console.log('login auth --->', authDataExt);
          setAuth(authDataExt);
        }
      });
    }
  }, [isWeb, setAuth]);

  // Extract the domain from the email address
  const extractEmailDomain = (email: string): string | null => {
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) return null; // Invalid email
    return email.slice(atIndex + 1);
  };

  const fetchTokenDetails = async (
    token: string
  ): Promise<GoogleTokenInfo | null> => {
    try {
      const response = await fetch(
        `${GoogleCredentialEndpoint}?access_token=${token}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch token details');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching token details:', error);
      return null;
    }
  };

  const saveAuth = async (auth: TokenResponse) => {
    if (!auth || (auth && !auth?.access_token)) {
      console.log('Require access token in the auth response...');
      return;
    }

    const tokenInfoCreds = await fetchTokenDetails(auth.access_token);

    if (
      !tokenInfoCreds?.user_id ||
      !tokenInfoCreds!.email ||
      !tokenInfoCreds!.verified_email
    ) {
      console.log('Token response => ', JSON.stringify(tokenInfoCreds));
      return;
    }

    // Validate email domain
    const emailDomain = extractEmailDomain(tokenInfoCreds.email);
    if (emailDomain === null || !emailDomain) {
      toast.error('Login failed. Email is invalid.', {
        position: 'bottom-right',
      });
      return;
    }
    if (!WHITELIST_DOMAIN_EMAILS.includes(emailDomain)) {
      console.log('Token credentials info', tokenInfoCreds);
      toast.error(
        'Login failed. Please use a miva.edu.ng or ulesson.com email to access the tools.',
        {
          position: 'bottom-right',
        }
      );
      return;
    }

    const userInfo: CredsAuthResponse = {
      access_token: auth.access_token,
      token_type: auth.token_type,
      expires_in: auth.expires_in,
      scope: auth.scope,
      prompt: auth.prompt,
      user_id: tokenInfoCreds?.user_id,
      email: tokenInfoCreds?.email,
      verified_email: tokenInfoCreds?.verified_email,
      access_type: tokenInfoCreds.access_type,
      state: auth.state,
      error: auth.error,
      error_description: auth.error_description,
      error_uri: auth.error_uri,
    };

    localStorage.setItem('auth', JSON.stringify(userInfo));
    setAuth(auth);
  };

  const clearAuth = () => {
    if (isWeb) {
      localStorage.removeItem('auth');
      localStorage.clear();
    } else {
      chrome.storage.local.clear();
    }
    setAuth({});
  };

  return (
    <AuthContext.Provider value={{ auth, saveAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export { PassAccessTokenProvider };
export default AuthContext;
